import classNames from 'classnames';
import SchedulingEvent from 'containers/scheduling/scheduling-event/SchedulingEvent/SchedulingEvent';
import { onDoubleClickCalendarSlot, SchedulingEventType } from 'containers/scheduling/types/types';
import { isDate } from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';
import * as React from 'react';
import { ReactNode } from 'react';
import { Calendar as BigCalendar, momentLocalizer, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { DAY } from 'utils/constants/timeInterval';
import '../Calendar.scss';
import CalendarToolbar from '../CalendarToolbar/CalendarToolbar';
import useCalendarTimeSlots from '../hooks/useCalendarTimeSlots';
import { CalendarView, ICalendarSlotInfo, OmittedDragAndDropCalendarProps } from '../types/CalendarTypes';
import { eventPropGetter } from '../utils/utils';

const localizer = momentLocalizer(moment);
const DndCalendar = withDragAndDrop<SchedulingEventType>(BigCalendar as any);

// TODO refactor for time off
export interface IDragAndDropCalendarProps extends OmittedDragAndDropCalendarProps<SchedulingEventType> {
  onEventClick?: (event: SchedulingEventType, mouseEvent: React.SyntheticEvent<HTMLElement>) => void;
  onRangeSelected?: (range: { start: Date | string; end: Date | string }, allowBreakReminders: boolean) => void;
  onDoubleClickEvent?: (event: SchedulingEventType) => void;
  onDoubleClickSlot?: onDoubleClickCalendarSlot;
  onDrillDown?: (date: Date, view: CalendarView) => void;
  onView?: (view: View, time?: DateTime | undefined) => void;
  events: SchedulingEventType[];
  date: DateTime;
  monthEvent: ReactNode;
  dayEvent: ReactNode;
  calendarToolbar: (() => ReactNode) | ReactNode;
  className?: string | object;
}

function DragAndDropCalendar(props: IDragAndDropCalendarProps) {
  const {
    onEventClick,
    onRangeSelected,
    onDoubleClickSlot,
    onDoubleClickEvent,
    onDrillDown,
    onView,
    calendarToolbar,
    date,
    monthEvent,
    dayEvent,
    className,
    view,
    events,
    ...calendarProps
  } = props;

  const { timeSlots, steps } = useCalendarTimeSlots();

  function onSelectSlot(slotInfo: ICalendarSlotInfo) {
    if (isDate(slotInfo.start) && isDate(slotInfo.end)) {
      // Calendar library started returning the end of the slot + 1 day instead of being the correct date
      // This may be a bug or may be intentional no idea
      // DAY is seconds, Date uses millis
      const adjustedEnd = new Date(slotInfo.end.getTime() - DAY * 1000);
      if (onRangeSelected && slotInfo.action === 'select') {
        onRangeSelected({ start: slotInfo.start, end: adjustedEnd }, true);
      } else if (onDoubleClickSlot && slotInfo.action === 'doubleClick') {
        onDoubleClickSlot({ start: slotInfo.start, end: adjustedEnd }, true);
      }
    }
  }

  function onDrill(date: Date, view: View) {
    if (view === 'month' || view === 'week' || view === 'day') {
      onDrillDown?.(date, view);
    }
  }

  const classes = classNames('calendar', view, className);

  return (
    <DndCalendar
      localizer={localizer}
      className={classes}
      eventPropGetter={eventPropGetter}
      onView={onView}
      resizable={true}
      timeslots={timeSlots}
      popup={true}
      date={date.toJSDate()}
      view={view}
      onDrillDown={onDrill}
      views={{ month: true, day: true }}
      components={{
        event: monthEvent as any,
        toolbar: calendarToolbar as any,
        month: {
          event: monthEvent as any,
        },
        day: {
          event: dayEvent as any,
        },
      }}
      events={events}
      onSelectEvent={onEventClick}
      onSelectSlot={onSelectSlot}
      onDoubleClickEvent={onDoubleClickEvent}
      step={steps}
      selectable={onRangeSelected ? true : false}
      dayLayoutAlgorithm={'no-overlap'}
      showMultiDayTimes={true}
      tooltipAccessor={() => null as any}
      {...calendarProps}
    />
  );
}

DragAndDropCalendar.defaultProps = {
  monthEvent: SchedulingEvent,
  weekEvent: SchedulingEvent,
  dayEvent: SchedulingEvent,
  calendarToolbar: CalendarToolbar,
};

export default DragAndDropCalendar;
